import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About — European Wig Luxury" },
      { name: "description", content: "European Wig Luxury was built on a single premise: premium virgin European hair, made with equal care for medical clients, modest wearers, and fashion buyers." },
      { property: "og:title", content: "About European Wig Luxury" },
      { property: "og:description", content: "One standard of craftsmanship. Every reason someone wears a wig." },
      { property: "og:url", content: "https://europeanwigluxury.com/about" },
      { property: "og:image", content: "https://europeanwigluxury.com/og-image.jpg" },
      { name: "twitter:image", content: "https://europeanwigluxury.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://europeanwigluxury.com/about" }],
  }),
  component: About,
});

function About() {
  return (
    <>
      <section className="border-b border-border/70 bg-secondary/40">
        <div className="container-luxe py-16 md:py-24">
          <p className="eyebrow">Our story</p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl md:text-6xl">One standard of craftsmanship. Every reason someone wears a wig.</h1>
        </div>
      </section>

      <section className="container-luxe grid gap-14 py-16 md:grid-cols-12 md:py-24">
        <div className="prose-luxe md:col-span-8 space-y-6 text-base leading-relaxed text-foreground/90 md:text-lg">
          <p>
            European Wig Luxury began with a frustration: the market for human-hair wigs was split between clinical
            medical-supply stores and loud, disposable fashion wigs. Both treated their customers as one thing —
            a patient, or a costume. Neither treated a wig as a serious, lasting piece.
          </p>
          <p>
            We started European Wig Luxury to hold every piece to the same standard, whether it's worn during chemotherapy,
            as part of a religious practice, over alopecia areata, or simply as a new haircut. The person under
            the wig changes. The craft doesn't.
          </p>
          <p>
            Our short-length wigs are made from 100% virgin European hair — never chemically
            processed, cuticles aligned root to tip, and hand-tied into a soft, breathable cap.
            Not because it markets well, but because those three things are the difference between a wig
            that lasts three years and one that lasts three months.
          </p>
          <p>
            We choose language carefully. We don't use the "grade" numbers (9A / 10A / 12A) the industry
            invented — that system is unregulated and largely meaningless. And when we talk about medical
            clients, we lead with dignity, not diagnosis.
          </p>
        </div>
        <aside className="md:col-span-4">
          <div className="rounded-md border border-border/70 bg-card p-7">
            <p className="eyebrow">We serve</p>
            <ul className="mt-4 space-y-3 text-sm">
              <li>· Clients with alopecia and thinning hair</li>
              <li>· Clients navigating chemotherapy and other medical hair loss</li>
              <li>· Religious and modest-wear clients</li>
              <li>· Clients choosing a wig as a styling piece</li>
            </ul>
            <p className="mt-6 text-sm text-muted-foreground">
              Same wig. Same craftsmanship. Same personal attention.
            </p>
            <Button asChild className="mt-6 w-full"><Link to="/contact">Talk to us</Link></Button>
          </div>
        </aside>
      </section>
    </>
  );
}
